import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import "../styles/landing.scss";
import { getCanonicalUrl } from "../utils/seoConfig";

function Pricing() {
  const { t } = useTranslation();
  const [billingCycle, setBillingCycle] = useState("yearly");
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const formatPrice = (price, cycle) => {
    if (price === "Custom" || price === "Individuell") return price;
    if (price === "$0" || price === "0€") return price;

    // Remove any existing /mo. suffix
    const cleanPrice = price.replace(/\/mo\.?$/, "");
    const currency = cleanPrice.includes("$") ? "$" : "€";
    const amount = cleanPrice.replace(/[^0-9]/g, "");

    // Always show as monthly price (e.g., $19/mo.)
    return `${currency}${amount}/mo.`;
  };

  const pricingPlans = useMemo(
    () => [
      {
        id: "free",
        name: t("FreePlan"),
        priceMonthly: t("FreePrice"),
        priceYearly: t("FreePrice"),
        description: t("FreeDescription"),
        features: [
          t("PricingFeature1"),
          t("PricingFeature2"),
          t("PricingFeature3"),
          t("PricingFeature4"),
        ],
        popular: false,
        isActive: false,
      },
      {
        id: "starter",
        name: t("StarterPlan"),
        priceMonthly: t("StarterPriceMonthly"),
        priceYearly: t("StarterPriceYearly"),
        description: t("StarterDescription"),
        features: [
          t("PricingFeature5"),
          t("PricingFeature6"),
          t("PricingFeature7"),
          t("PricingFeature8"),
          t("PricingFeature9"),
          t("PricingFeature10"),
          t("PricingFeature11"),
          t("PricingFeature12"),
        ],
        popular: false,
        isActive: false,
      },
      {
        id: "pro",
        name: t("ProPlan"),
        priceMonthly: t("ProPriceMonthly"),
        priceYearly: t("ProPriceYearly"),
        description: t("ProDescription"),
        features: [
          t("PricingFeature13"),
          t("PricingFeature14"),
          t("PricingFeature15"),
          t("PricingFeature16"),
          t("PricingFeature17"),
          t("PricingFeature18"),
          t("PricingFeature11"),
          t("PricingFeature12"),
          t("PricingFeature19"),
        ],
        popular: true,
        isActive: false,
      },
      {
        id: "pro_plus",
        name: t("ProPlusPlan"),
        priceMonthly: t("ProPlusPriceMonthly"),
        priceYearly: t("ProPlusPriceYearly"),
        description: t("ProPlusDescription"),
        features: [
          t("PricingFeature20"),
          t("PricingFeature21"),
          t("PricingFeature22"),
          t("PricingFeature23"),
          t("PricingFeature24"),
          t("PricingFeature25"),
          t("PricingFeature11"),
          t("PricingFeature12"),
          t("PricingFeature26"),
        ],
        popular: false,
        isActive: false,
      },
      {
        id: "enterprise",
        name: t("EnterprisePlan"),
        priceMonthly: t("EnterprisePrice"),
        priceYearly: t("EnterprisePrice"),
        description: t("EnterpriseDescription"),
        features: [
          t("PricingFeature27"),
          t("PricingFeature28"),
          t("PricingFeature29"),
          t("PricingFeature30"),
          t("PricingFeature31"),
          t("PricingFeature32"),
          t("PricingFeature33"),
          t("PricingFeature11"),
          t("PricingFeature12"),
          t("PricingFeature34"),
        ],
        popular: false,
        isActive: false,
      },
    ],
    [t]
  );

  const faqs = [
    {
      question: t("PricingFaq1Question"),
      answer: t("PricingFaq1Answer"),
    },
    {
      question: t("PricingFaq2Question"),
      answer: t("PricingFaq2Answer"),
    },
    {
      question: t("PricingFaq3Question"),
      answer: t("PricingFaq3Answer"),
    },
    {
      question: t("PricingFaq4Question"),
      answer: t("PricingFaq4Answer"),
    },
  ];

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <>
      <Helmet>
        <title>{t("PricingPageTitle")}</title>
        <meta name="description" content={t("PricingPageDescription")} />
        <meta name="keywords" content={t("PricingPageKeywords")} />
        <link rel="canonical" href={getCanonicalUrl("/pricing")} />
      </Helmet>
      <div className="App">
        <section className="section pricing-plans">
          <div className="pricing-plans-header">
            <div className="pricing-product-badge">
              {t("TimeSeriesLabeling")}
            </div>
            <h1 className="pricing-plans-title">{t("Pricing")}</h1>
            <p className="pricing-plans-description">
              {t("PricingHeroDescription")}
            </p>
          </div>

          <div className="pricing-plans-wrapper">
            <div className="pricing-billing-toggle">
              <div
                className="pricing-billing-toggle-slider"
                data-active={billingCycle}
              >
                <div className="pricing-billing-toggle-slider-track"></div>
              </div>
              <button
                type="button"
                className={`pricing-billing-toggle-option ${
                  billingCycle === "monthly"
                    ? "pricing-billing-toggle-option--active"
                    : ""
                }`}
                onClick={() => setBillingCycle("monthly")}
              >
                {t("Monthly")}
              </button>
              <button
                type="button"
                className={`pricing-billing-toggle-option ${
                  billingCycle === "yearly"
                    ? "pricing-billing-toggle-option--active"
                    : ""
                }`}
                onClick={() => setBillingCycle("yearly")}
              >
                {t("Yearly")}
              </button>
            </div>

            <div className="pricing-grid-new">
              {pricingPlans.map((plan) => (
                <div
                  key={`${plan.id}-${billingCycle}`}
                  className={`pricing-card-new ${
                    plan.isActive ? "pricing-card-new--active" : ""
                  } ${plan.popular ? "pricing-card-new--popular" : ""}`}
                >
                  {plan.popular && (
                    <div className="pricing-card-new-badge">{t("Popular")}</div>
                  )}
                  {plan.isActive && (
                    <div className="pricing-card-new-current-badge">
                      {t("CurrentPlan")}
                    </div>
                  )}
                  <div className="pricing-card-new-header">
                    <h3 className="pricing-card-new-title">{plan.name}</h3>
                    <div className="pricing-card-new-price">
                      {(() => {
                        // Get the correct price based on billing cycle - fetch fresh from translations
                        let currentPrice;
                        if (plan.id === "enterprise") {
                          currentPrice = t("EnterprisePrice");
                        } else if (plan.id === "free") {
                          currentPrice = t("FreePrice");
                        } else if (billingCycle === "monthly") {
                          if (plan.id === "starter") {
                            currentPrice = t("StarterPriceMonthly");
                          } else if (plan.id === "pro") {
                            currentPrice = t("ProPriceMonthly");
                          } else if (plan.id === "pro_plus") {
                            currentPrice = t("ProPlusPriceMonthly");
                          }
                        } else {
                          if (plan.id === "starter") {
                            currentPrice = t("StarterPriceYearly");
                          } else if (plan.id === "pro") {
                            currentPrice = t("ProPriceYearly");
                          } else if (plan.id === "pro_plus") {
                            currentPrice = t("ProPlusPriceYearly");
                          }
                        }

                        // For Enterprise/Custom, return as is
                        if (
                          currentPrice === t("EnterprisePrice") ||
                          currentPrice === "Individuell"
                        ) {
                          return (
                            <span className="pricing-card-new-amount">
                              {currentPrice}
                            </span>
                          );
                        }

                        // Format and display the price
                        return (
                          <span className="pricing-card-new-amount">
                            {formatPrice(currentPrice, billingCycle)}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <p className="pricing-card-new-description">
                    {plan.description}
                  </p>
                  <ul className="pricing-card-new-features">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="pricing-card-new-feature">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="pricing-card-new-checkmark"
                        >
                          <path
                            d="M5 13L9 17L19 7"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section faq-section">
          <div className="faq-wrapper">
            <h2 className="section-title">{t("PricingFAQTitle")}</h2>
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`faq-item ${openFaqIndex === index ? "open" : ""}`}
                >
                  <button
                    className="faq-question"
                    onClick={() => toggleFaq(index)}
                  >
                    <span>{faq.question}</span>
                    <svg
                      className="faq-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Pricing;
